<template>
    <v-dialog v-model="dialog.show" max-width="1000px" persistent scrollable>
        <v-card class="global-discount-card">
            <v-card-title class="font-weight-bold">Distribuir descuento del documento</v-card-title>
            <v-card-text v-if="dialog.document" class="global-discount-card__body">
                <p>
                    <strong>Documento:</strong>
                    {{ dialog.document.identificacion?.numeroControl || dialog.document.identificacion?.codigoGeneracion }}
                    <br>
                    <strong>Proveedor:</strong> {{ dialog.document.emisor?.nombre || 'No disponible' }}
                    <br>
                    <strong>Descuento global:</strong> {{ dialog.totalDiscount.toFixed(2) }}
                </p>
                <v-checkbox
                    v-model="dialog.applyToAll"
                    label="Aplicar descuento a todos los productos"
                    @change="toggleAll"
                ></v-checkbox>
                <v-alert v-if="dialog.error" type="warning" dense outlined>
                    {{ dialog.error }}
                </v-alert>
                <v-simple-table dense>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Seleccionar</th>
                                <th>Producto</th>
                                <th>Cantidad</th>
                                <th class="text-right">Original</th>
                                <th class="text-right">Descuento</th>
                                <th class="text-right">Resultado</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(line, index) in dialog.lines" :key="index">
                                <td>
                                    <v-checkbox v-model="line.selected" @change="recalculate" hide-details></v-checkbox>
                                </td>
                                <td>{{ line.description }}</td>
                                <td>{{ line.quantity }}</td>
                                <td class="text-right">{{ (line.original / 100).toFixed(2) }}</td>
                                <td class="text-right">{{ (line.discount / 100).toFixed(2) }}</td>
                                <td class="text-right">{{ (line.result / 100).toFixed(2) }}</td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
                <div class="text-right mt-3">
                    Descuento distribuido: {{ dialog.distributed.toFixed(2) }}
                    <br>
                    Pendiente de distribuir: {{ dialog.pending.toFixed(2) }}
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="cancel">Cancelar</v-btn>
                <v-btn color="primary" @click="confirm" :disabled="!dialog.canConfirm">Confirmar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    calculateDiscountPreview,
    getDiscountedDetails,
    getDiscountLines,
    getGlobalDiscounts,
    getProcessingIva,
    getAdjustedTotals,
    toCents
} from './globalDiscountCalculator';

export default {
    name: 'GlobalDiscountDistribution',
    data() {
        return {
            dialog: {
                show: false,
                document: null,
                lines: [],
                totalDiscount: 0,
                distributed: 0,
                pending: 0,
                applyToAll: true,
                error: '',
                canConfirm: false,
                resolve: null
            }
        };
    },
    methods: {
        toCents,
        getGlobalDiscounts,
        hasItemDiscount(document) {
            const details = Array.isArray(document?.cuerpoDocumento)
                ? document.cuerpoDocumento
                : [];

            return details.some(detail => {
                const value = detail?.montoDescu;
                if (value === null || value === undefined) return false;
                if (typeof value === 'string' && value.trim() === '') return false;
                return Number(value) !== 0 || Number.isNaN(Number(value));
            });
        },
        getTotalGlobalDiscount(document) {
            return getGlobalDiscounts(document)
                .reduce((sum, discount) => sum + discount.amount, 0) / 100;
        },
        getDiscountLines,
        getProcessingIva,
        getAdjustedTotals,
        getDiscountedDetails,
        calculateDiscountPreview,
        prepareProcessingData(document, details) {
            const originalDetails = Array.isArray(document?.cuerpoDocumento)
                ? document.cuerpoDocumento
                : [];
            if (!getGlobalDiscounts(document).length) {
                return {
                    details: originalDetails,
                    totals: null,
                    dteJson: { ...document, cuerpoDocumento: originalDetails.map(detail => ({ ...detail })) }
                };
            }
            const totals = getAdjustedTotals(document, details);
            return {
                details,
                totals,
                dteJson: {
                    ...document,
                    resumen: { ...document.resumen, ...totals },
                    cuerpoDocumento: details.map(detail => ({ ...detail }))
                }
            };
        },
        async processDocument(document) {
            const originalDetails = Array.isArray(document?.cuerpoDocumento)
                ? document.cuerpoDocumento
                : [];
            const discounts = getGlobalDiscounts(document);
            if (
                document?.tipoDte === '09'
                || !discounts.length
                || !originalDetails.length
                || this.hasItemDiscount(document)
            ) {
                return {
                    details: originalDetails,
                    totals: null,
                    dteJson: { ...document, cuerpoDocumento: originalDetails.map(detail => ({ ...detail })) }
                };
            }

            const lines = getDiscountLines(document);
            if (!lines.length) throw new Error('El documento tiene descuento global, pero no contiene productos elegibles.');
            this.dialog = {
                ...this.dialog,
                show: lines.length > 1,
                document,
                lines,
                totalDiscount: this.getTotalGlobalDiscount(document),
                applyToAll: true,
                error: '',
                resolve: null
            };
            this.recalculate();

            if (lines.length > 1) {
                const confirmed = await new Promise(resolve => { this.dialog.resolve = resolve; });
                if (!confirmed) throw new Error('Distribución de descuento cancelada.');
            } else if (!this.dialog.canConfirm) {
                throw new Error(this.dialog.error);
            }

            const details = getDiscountedDetails(document, this.dialog.lines);
            return this.prepareProcessingData(document, details);
        },
        recalculate() {
            Object.assign(this.dialog, calculateDiscountPreview(this.dialog.document, this.dialog.lines));
        },
        toggleAll() {
            this.dialog.lines.forEach(line => { line.selected = this.dialog.applyToAll; });
            this.recalculate();
        },
        confirm() {
            if (this.dialog.canConfirm && this.dialog.resolve) {
                const resolve = this.dialog.resolve;
                this.dialog.resolve = null;
                this.dialog.show = false;
                resolve(true);
            }
        },
        cancel() {
            if (this.dialog.resolve) {
                const resolve = this.dialog.resolve;
                this.dialog.resolve = null;
                this.dialog.show = false;
                resolve(false);
            }
        }
    }
};
</script>

<style scoped>
.global-discount-card__body {
    max-height: calc(100vh - 190px);
    overflow-y: auto;
}
</style>
